import { CONTACT_EMAIL } from 'app/../app.config.json';
import { withErrorBoundary } from 'app/components/error-boundary';
import { InsuredUser } from 'app/components/insured-users';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import {
  IconAlertTriangle,
  IconHeart,
  IconHelpCircle,
  IconMail,
  IconMessages,
  IconTruck,
} from '@tabler/icons-react-native';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { getI18nLanguage } from 'app/lib/language';
import { safelyOpenURL } from 'app/lib/navigation';
import { preparePolicyUrl } from 'app/lib/text';
import { SUPPORT_CHANNEL_URL, SUPPORT_TRANSPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useMyProfile } from 'app/network/profile';
import { useUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TextMessageMessage } from 'app/types/chat';
import type { NavigationScreen } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useAlignmentIfDesktop, usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect } from 'react';
import { Pressable } from 'react-native';
const theme = createDSTheme();

const ContactUsScreenComponent: NavigationScreen<'contact-us'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const alignment = useAlignmentIfDesktop();

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );
  const setPageTitle = usePageTitle('contact-us');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { data: user, isPending: isUserLoading } = useUser();
  const { data: myProfile } = useMyProfile();

  const canUseHelpToTransport = !isUserLoading && isFeatureEnabled(FeatureFlag.helpToTransport, user?.featureFlags);

  const goToSupportChat = (messagePrompt: TextMessageMessage) => {
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, messagePrompt);
  };

  const ctas = [
    {
      icon: <IconMessages color={theme.colors['text.primary']} strokeWidth={ICON_STROKE_WIDTH} />,
      label: t('chatCTALabel'),
      onPress: () => navigateToChat(navigation, SUPPORT_CHANNEL_URL),
    },
    ...(canUseHelpToTransport
      ? [
          {
            icon: <IconTruck strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />,
            label: t('transportCTALabel'),
            onPress: () => navigateToChat(navigation, SUPPORT_TRANSPORT_CHANNEL_URL),
          },
        ]
      : []),
    {
      icon: <IconMail color={theme.colors['text.primary']} strokeWidth={ICON_STROKE_WIDTH} />,
      label: t('emailCTALabel'),
      onPress: () => void safelyOpenURL(`mailto:${CONTACT_EMAIL}`),
    },
    {
      icon: <IconHelpCircle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />,
      label: t('faqCTALabel'),
      onPress: () => void safelyOpenURL(preparePolicyUrl(t('faqLink'), i18nLanguage, user)),
    },
    {
      icon: <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />,
      label: t('bugReportCTALabel'),
      onPress: () =>
        goToSupportChat({
          i18nKey: `chatPrompt.bugReport`,
          ns: 'contact-us',
          values: { name: myProfile?.firstName || '' },
        }),
    },
    {
      icon: <IconHeart strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />,
      label: t('suggestionCTALabel'),
      onPress: () =>
        goToSupportChat({
          i18nKey: `chatPrompt.suggestion`,
          ns: 'contact-us',
          values: { name: myProfile?.firstName || '' },
        }),
    },
  ];

  return (
    <Page title={t('title')}>
      <Column gap="spacing.general.x4">
        <Text textAlign={alignment} variant="bodyBase">
          {t('firstParagraph')}
        </Text>

        <Column
          gap="spacing.general.x4"
          padding="spacing.general.x4"
          backgroundColor="surface.tertiary-neutral"
          borderRadius="radius.lg"
        >
          {ctas.map(({ icon, label, onPress }) => (
            <Pressable key={label} onPress={onPress}>
              <Row alignItems="center" gap="spacing.general.x2">
                <Column alignItems="center" width={32}>
                  {icon}
                </Column>
                <Text variant="bodyBase">{label}</Text>
              </Row>
            </Pressable>
          ))}
        </Column>

        <InsuredUser otherUser={undefined} placement="help" />
      </Column>
    </Page>
  );
};

export const ContactUsScreen: NavigationScreen<'contact-us'> = withErrorBoundary(
  withDashboardLayout(ContactUsScreenComponent),
);
